import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, categoryLabel, fmtMajor, fmtMinor, fmtDate, todayLocal,
  CATEGORIES, EXAMPLE_QUESTIONS,
  type Account, type Txn, type Overview, type QuerySlice, type ParsePreview,
  type HeadlineFact, type FactTone,
} from "./store";
import { answerQuestion, draftRecap, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import { buildExport } from "./export";
import Chat from "./chat/Chat";
import {
  Sparkles, Upload, FileText, Receipt, Wallet, Trash2, X, Lock, WifiOff,
  Search, Send, Repeat, Landmark, RefreshCw, TrendingUp, TrendingDown,
  Coins, CalendarClock, ArrowDownUp,
} from "lucide-react";

import glyphBriefing from "./assets/glyph-briefing.svg";
import glyphRepeat from "./assets/glyph-repeat.svg";
import emptyImport from "./assets/empty-import.svg";

const APP_NAME = "Oriora Statements";
const SCHEME = "oriora-statements";

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

/**
 * A statement slip: ruled entry lines with the amount column set apart on the right — what an
 * imported transaction list looks like on paper. Replaces the lucide `Sparkles` shared by 20 of
 * the fleet's 26 chat launchers; the outline changes with it, because the same glyph in a
 * recoloured pill still reads as the same control across two of our apps (Guideline 4.3(a)).
 */
function SlipMark({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M6 7h8M6 12h8M6 17h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M18 6v12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity="0.55" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid JWT, so getSession() still returns it.
  // getUser() asks the SERVER — on a real 401/403 we purge the dead session and drop to login.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches deleted-account-came-back).
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-statements://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window navigated to oriora-statements://deleted.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand={APP_NAME}
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <BootSplash />;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function BootSplash() {
  return (
    <div style={{ height: "100dvh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
      <div style={{ display: "grid", placeItems: "center", gap: "0.9rem" }}>
        <div className="empty-art" style={{ width: 64, height: 64, borderRadius: 18 }}><Landmark size={30} /></div>
        <div className="skeleton-line" style={{ width: 120, height: 10 }} />
      </div>
    </div>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card fade-up" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "1rem" }}><Landmark size={34} /></div>
        <div className="eyebrow" style={{ justifyContent: "center" }}>{APP_NAME}</div>
        <h1 className="serif" style={{ fontSize: "1.55rem", margin: "0.5rem 0 0.5rem", letterSpacing: "-0.02em" }}>Your statements, understood</h1>
        <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
          Import your bank and card statements — every transaction is parsed and totalled on this device.
          Oriora narrates the figures when you ask, through your own account.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 48 }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const p = platform();
              if (p === "ios") await signInIos(SCHEME);
              else if (p === "macos") await signInMac(SCHEME);
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/oriora-statements/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
        <p className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "1rem", lineHeight: 1.5 }}>
          Opens Oriora to sign in, then returns here. Your statements stay on your device unless you back them up.
        </p>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [confirmDeleteAcc, setConfirmDeleteAcc] = useState<Account | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not change
  // any existing screen. onChanged → reload() refreshes the ledger after a chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);
  const askRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [accs, ov, list] = await Promise.all([
        store.listAccounts(),
        store.computeOverview(),
        store.listTxns({ limit: 200 }),
      ]);
      setAccounts(accs);
      setOverview(ov);
      setTxns(list);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (macOS menu bar): Import / Ask.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      if (e.payload === "import") setShowImport(true);
      if (e.payload === "ask") askRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const accountName = useCallback((id: string) => accounts.find((a) => a.id === id)?.name ?? "Account", [accounts]);
  const hasData = (overview?.total_txns ?? 0) > 0;

  const doExport = useCallback(async () => {
    const all = await store.listTxns({ limit: 5_000_000 });
    if (all.length === 0) return;
    const { csv, filename } = buildExport(all, accountName);
    const p = await saveFileDialog({ defaultPath: filename });
    if (p) await writeTextFile(p, csv);
  }, [accountName]);

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      padding: "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="notice notice-warn" style={{ marginBottom: "1rem" }}>
          <WifiOff size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span><strong>Offline.</strong> Asking is paused — importing and totals still work on this device.</span>
        </div>
      )}

      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem" }}>
        <div className="row" style={{ gap: "0.55rem" }}>
          <div className="tile-icon" style={{ width: 34, height: 34, borderRadius: 10 }}><img src={glyphBriefing} width={20} height={20} alt="" /></div>
          <h1 className="serif" style={{ fontSize: "1.4rem", margin: 0, letterSpacing: "-0.02em" }}>Statements</h1>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BackupMenu
            appName="Statements" fileSlug="statements" appScheme="oriora-statements"
            hasData={hasData} onRestored={reload}
            onExportCsv={hasData ? () => void doExport() : undefined}
          />
          <AccountMenu email={email} appSlug="oriora-statements" appName="Oriora Statements" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      {loading ? (
        <LoadingHome />
      ) : !hasData ? (
        <EmptyState onImport={() => setShowImport(true)} />
      ) : (
        <>
          {/* R1 — the briefing leads: the spending told in sentences, receipts on tap. */}
          <Briefing overview={overview!} txns={txns} />

          {/* R2 — what repeats, elevated as worded cards. */}
          {overview!.recurring.length > 0 && <RecurringCard overview={overview!} />}

          {/* R4 — import demoted to a quiet affordance once data exists. */}
          <div className="row" style={{ justifyContent: "flex-end", margin: "0.5rem 0 0.2rem" }}>
            <button className="import-quiet" onClick={() => setShowImport(true)}><Upload size={13} /> Import another statement</button>
          </div>

          {/* Where it went — the drill-down behind the words. */}
          {overview!.by_category.length > 0 && <CategoryBreakdown overview={overview!} />}

          <div ref={askRef}>
            <AskCard offline={offline} />
          </div>

          <AccountsSection accounts={accounts} overview={overview!} onImport={() => setShowImport(true)} onDelete={setConfirmDeleteAcc} />

          <div className="sec-head">
            <h2><Receipt size={17} style={{ color: "var(--accent)" }} /> Recent transactions</h2>
          </div>
          <TxnList txns={txns} accountName={accountName} onChanged={() => void reload()} />
        </>
      )}

      {showImport &&<ImportSheet onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); void reload(); }} accounts={accounts} />}

      

      {confirmDeleteAcc && (
        <CenterModal title="Delete account & its transactions?" onClose={() => setConfirmDeleteAcc(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.5 }}>
            Remove “{confirmDeleteAcc.name}” and every transaction imported under it from this device? This can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmDeleteAcc(null)}>Cancel</button>
            <button className="btn btn-primary btn-pill" style={{ background: "var(--rose)", borderColor: "var(--rose)" }}
              onClick={async () => { await store.deleteAccount(confirmDeleteAcc.id); setConfirmDeleteAcc(null); void reload(); }}>Delete</button>
          </div>
        </CenterModal>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the view. */}
      {!chatOpen && (
        <button className="stc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <SlipMark size={19} />
        </button>
      )}
      <aside className={`stc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="stc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — ask, search, recategorize</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={() => void reload()} />
      </aside>
    </main>
  );
}

function LoadingHome() {
  return (
    <div className="fade-up">
      {[0, 1, 2].map((i) => <div key={i} className="skeleton-block" style={{ height: 96, borderRadius: "var(--radius-lg)", marginBottom: "0.65rem" }} />)}
      <div className="card" style={{ padding: "1.1rem 1.25rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 10 }}>
        <div className="skeleton-line" style={{ width: "40%" }} />
        <div className="skeleton-line" style={{ width: "90%" }} />
        <div className="skeleton-line" style={{ width: "70%" }} />
      </div>
    </div>
  );
}

function EmptyState({ onImport }: { onImport: () => void }) {
  return (
    <div className="card fade-up" style={{ padding: "2.75rem 1.75rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
      <div className="empty-art" style={{ width: 120, height: 96, background: "none" }}><img src={emptyImport} alt="" style={{ width: 120, height: 90 }} /></div>
      <div className="serif" style={{ fontSize: "1.4rem", margin: "1rem 0 0.4rem", letterSpacing: "-0.02em" }}>Import your first statement</div>
      <div className="muted" style={{ fontSize: "var(--fs-sm)", marginBottom: "1.5rem", lineHeight: 1.6, maxWidth: 340, marginInline: "auto" }}>
        Bring in a bank or card statement (CSV, or PDF for supported layouts). Every transaction is parsed and
        totalled on this device — then ask plain-English questions about your spending.
      </div>
      <button className="btn btn-primary btn-pill" style={{ margin: "0 auto", minHeight: 46, padding: "0 1.5rem" }} onClick={onImport}>
        <Upload size={17} /> Import a statement
      </button>

      {/* READY-STATE R1 — why this app is shaped this way, on the FIRST screen.
          These are the PARITY-MATRIX "## HAVE (and structurally ahead)" rows, in its own words.
          Before this, a new user met one door and a template card: nothing said that the whole
          point is that you bring the FILE, so no bank credentials are ever shared — which is the
          one difference the leaders' own pricing pages make expensive (Monarch's own page:
          "Monarch Money may cost $99 a year", and it is a link-your-bank product).
          Each claim below is code, not aspiration:
            no credentials      — the only input is a file you choose (the import sheet)
            on-device category  — categorize.rs:194, a keyword map, "No AI, no network";
                                  every one overridable (db.rs:239 set_category)
            repeats on device   — rollups.rs:209 detect_recurring, weekly/monthly bands
          DELIBERATELY ABSENT: any claim that a bank link "breaks". That is review-derived,
          unverifiable from a vendor's own pages, and must not reach customer copy. */}
      <ul className="why-list">
        <li><strong>Nothing to link.</strong> You bring the file — no bank credentials are shared.</li>
        <li><strong>Sorted on this device.</strong> Categories come from a fixed keyword list, not a model, and you can change any of them.</li>
        <li><strong>Repeats found for you.</strong> Weekly and monthly patterns are spotted from your own rows.</li>
        <li><strong>No subscription.</strong> Importing and totalling are free; only the questions you ask cost anything.</li>
      </ul>
    </div>
  );
}

// R1 — THE BRIEFING: the spending told in sentences, computed free from the on-device
// rollups. Each sentence's numbers are its receipt — tap to unfold the transactions
// behind it. Facts only, never advice (fence.ts governs the AI; this copy states data).
function Fig({ children }: { children: React.ReactNode }) {
  return <span className="briefing-figure tnum">{children}</span>;
}
function Briefing({ overview, txns }: { overview: Overview; txns: Txn[] }) {
  const cur = overview.currency;
  const [open, setOpen] = useState<number | null>(null);
  const range = overview.date_range;
  const spanDays = range ? Math.max(1, Math.round((+new Date(range[1]) - +new Date(range[0])) / 86400000) + 1) : 0;
  const spanTxt = spanDays >= 55 ? `these ${Math.round(spanDays / 30)} months` : spanDays >= 13 ? `these ${Math.round(spanDays / 7)} weeks` : `these ${spanDays} days`;
  const topCat = overview.by_category[0];
  const topM = overview.top_merchants[0];

  const sentences: { p: React.ReactNode; caption: string; receipts: Txn[] }[] = [];
  sentences.push({
    p: (<>You spent <Fig>{fmtMajor(overview.total_spend, cur)}</Fig> {spanTxt}{topCat ? <> — {categoryLabel(topCat.category).toLowerCase()} led at <Fig>{fmtMajor(topCat.spend, cur)}</Fig></> : null}.</>),
    caption: topCat ? `${categoryLabel(topCat.category)} — the largest of it` : "The largest transactions",
    receipts: (topCat ? txns.filter((t) => t.category === topCat.category && t.amount < 0) : txns.filter((t) => t.amount < 0)).slice(0, 6),
  });
  if (topM && topM.txn_count >= 2) {
    sentences.push({
      p: (<>{topM.merchant} keeps coming back: <Fig>{topM.txn_count} visits</Fig>, about <Fig>{fmtMajor(topM.spend / topM.txn_count, cur)}</Fig> a time.</>),
      caption: `${topM.merchant} — the visits`,
      receipts: txns.filter((t) => t.merchant === topM.merchant).slice(0, 6),
    });
  }
  if (overview.total_income > 0) {
    const net = overview.total_income - overview.total_spend;
    sentences.push({
      p: (<>Money came in at <Fig>{fmtMajor(overview.total_income, cur)}</Fig>{net >= 0
        ? <> — leaving <Fig>{fmtMajor(net, cur)}</Fig> more in than out.</>
        : <> — spending ran <Fig>{fmtMajor(-net, cur)}</Fig> ahead of it.</>}</>),
      caption: "Money in — the deposits",
      receipts: txns.filter((t) => t.amount > 0).slice(0, 6),
    });
  }

  return (
    <div className="briefing fade-up">
      {sentences.map((s, i) => (
        <div key={i} className={`briefing-sentence${open === i ? " open" : ""}`}
          role="button" tabIndex={0}
          onClick={() => setOpen(open === i ? null : i)}
          onKeyDown={(e) => { if (e.key === "Enter") setOpen(open === i ? null : i); }}>
          <p>{s.p}</p>
          <div className="briefing-caption"><span className="postmark" /> computed on this device · tap for the receipts</div>
          {open === i && s.receipts.length > 0 && (
            <div className="briefing-receipts">
              <div className="drilldown-lbl">{s.caption}</div>
              {s.receipts.map((t) => (
                <div key={t.id} className="receipt-row">
                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.merchant} <span className="muted">· {fmtDate(t.posted_on)}</span></span>
                  <span className={`tnum ${t.amount < 0 ? "amt-out" : "amt-in"}`} style={{ fontWeight: 600, flexShrink: 0 }}>{fmtMinor(t.amount, t.currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// SummaryHero + the 4-tile InsightsPanel stat grid are RETIRED (a3 R3) — their figures
// live in the briefing sentences now.
function SummaryHero({ overview, onImport }: { overview: Overview; onImport: () => void }) {
  const cur = overview.currency;
  const range = overview.date_range ? `${fmtDate(overview.date_range[0])} – ${fmtDate(overview.date_range[1])}` : "";
  return (
    <div className="hero fade-up" style={{ marginBottom: "1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", position: "relative", zIndex: 1 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "var(--fs-xs)", opacity: 0.8, fontWeight: 600 }}>Total spending</div>
          <div className="serif tnum" style={{ fontSize: "2rem", fontWeight: 600, lineHeight: 1.05, letterSpacing: "-0.02em", marginTop: 2 }}>{fmtMajor(overview.total_spend, cur)}</div>
          <div style={{ fontSize: "var(--fs-xs)", opacity: 0.78, marginTop: 4 }}>
            {overview.total_txns.toLocaleString()} transactions{range ? ` · ${range}` : ""}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: "var(--fs-xs)", opacity: 0.8, fontWeight: 600 }}>Money in</div>
          <div className="tnum" style={{ fontSize: "1.15rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{fmtMajor(overview.total_income, cur)}</div>
          <button className="btn btn-sm btn-pill" style={{ marginTop: 10, background: "rgba(255,255,255,0.55)", borderColor: "transparent", color: "var(--accent-deep)" }} onClick={onImport}>
            <Upload size={12} /> Add more
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Insights panel: the computed-figures dashboard (deterministic, no AI call) ──
function InsightsPanel({ overview }: { overview: Overview }) {
  const cur = overview.currency;
  const months = overview.by_month;
  const monthsTracked = Math.max(months.length, 1);
  const avgPerMonth = overview.total_spend / monthsTracked;
  const recurringMonthly = useMemo(
    () => overview.recurring.reduce((acc, r) => acc + (r.cadence === "monthly" ? r.typical_amount : r.cadence === "weekly" ? r.typical_amount * 4.33 : 0), 0),
    [overview.recurring],
  );

  // Month-over-month for the two most recent months (deterministic).
  const lastTwo = months.slice(-2);
  const momDelta = lastTwo.length === 2 ? lastTwo[1].spend - lastTwo[0].spend : null;
  const momPct = lastTwo.length === 2 && lastTwo[0].spend > 0 ? (momDelta! / lastTwo[0].spend) * 100 : null;

  return (
    <div className="fade-up" style={{ marginBottom: "1.1rem" }}>
      <div className="stat-grid" style={{ marginBottom: months.length > 0 || overview.by_category.length > 0 ? "0.7rem" : 0 }}>
        <StatCard
          label={<><CalendarClock size={13} /> Spent / month</>}
          value={fmtMajor(avgPerMonth, cur)}
          sub={`${monthsTracked} ${monthsTracked === 1 ? "month" : "months"} tracked`}
        />
        <StatCard
          label={<><ArrowDownUp size={13} /> Latest month</>}
          value={months.length > 0 ? fmtMajor(months[months.length - 1].spend, cur) : "—"}
          trend={momDelta !== null && Math.abs(momDelta) > 0.5
            ? { dir: momDelta > 0 ? "up" : "down", good: momDelta < 0, label: momPct !== null ? `${momPct > 0 ? "+" : ""}${Math.round(momPct)}%` : `${momDelta > 0 ? "+" : "-"}${fmtMajor(Math.abs(momDelta), cur)}` }
            : null}
          sub={months.length < 2 ? "vs prior month once 2+ tracked" : undefined}
        />
        <StatCard
          label={<><Repeat size={13} /> Recurring / month</>}
          value={recurringMonthly > 0 ? fmtMajor(recurringMonthly, cur) : "—"}
          sub={overview.recurring.length > 0 ? `${overview.recurring.length} detected` : "none detected yet"}
        />
        <StatCard
          label={<><Wallet size={13} /> Net</>}
          value={fmtMajor(overview.total_income - overview.total_spend, cur)}
          sub="money in − out"
        />
      </div>

      {overview.by_category.length > 0 && <CategoryBreakdown overview={overview} />}
    </div>
  );
}

function StatCard({ label, value, trend, sub }: {
  label: React.ReactNode; value: string;
  trend?: { dir: "up" | "down"; good: boolean; label: string } | null; sub?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-lbl">{label}</div>
      <div className="stat-val tnum">{value}</div>
      {trend && trend.label ? (
        <div className="stat-sub tnum" style={{ color: trend.good ? "var(--good)" : "var(--bad)" }}>
          {trend.dir === "up" ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {trend.label}
        </div>
      ) : sub ? (
        <div className="stat-sub tnum">{sub}</div>
      ) : null}
    </div>
  );
}

function CategoryBreakdown({ overview }: { overview: Overview }) {
  const cur = overview.currency;
  const top = overview.by_category.slice(0, 6);
  const max = Math.max(...top.map((c) => c.spend), 1);
  return (
    <div className="card" style={{ padding: "1rem 1.1rem", marginTop: "0.7rem" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.7rem" }}>Where it went</div>
      {top.map((c) => (
        <div key={c.category} className="spend-row">
          <span className="spend-name">{categoryLabel(c.category)}</span>
          <div className="spend-track"><div className="spend-fill" style={{ width: `${Math.max(6, (c.spend / max) * 100)}%` }} /></div>
          <span className="spend-val tnum">{fmtMajor(c.spend, cur)}</span>
        </div>
      ))}
    </div>
  );
}

/** WHAT REPEATS (ledger row 2b).
 *
 *  rollups.rs sorts recurring charges by TYPICAL AMOUNT, so a £100 annual charge outranks a £15
 *  monthly subscription that costs £180 a year — the list answered "which single charge is
 *  biggest" when the question is "what is costing me most". Re-ranked here by ANNUALISED cost,
 *  which is the size of the actual decision, and this card now LEADS the screen instead of sitting
 *  below the spend hero and the category breakdown.
 *
 *  🔒 Descriptive only — it reports charges the statements show. It never suggests cancelling
 *  anything, never calls a charge too high, and never predicts; that judgement is regulated
 *  financial advice and is blocked pre-LLM by src/fence.ts. */
function RecurringCard({ overview }: { overview: Overview }) {
  const cur = overview.currency;
  // "irregular" has no reliable period, so it annualises to 0 and never outranks a known cadence.
  const annual = (r: Overview["recurring"][number]) =>
    r.cadence === "monthly" ? r.typical_amount * 12 : r.cadence === "weekly" ? r.typical_amount * 52 : 0;
  const ranked = [...overview.recurring].sort((a, b) => annual(b) - annual(a) || b.occurrences - a.occurrences);
  const yearly = ranked.reduce((t, r) => t + annual(r), 0);
  const rows = ranked.slice(0, 6);
  // R2 — worded cards: each repeat reads as a sentence with its cadence and annual cost.
  return (
    <div style={{ marginTop: "0.2rem" }}>
      <div className="sec-head" style={{ margin: "0.6rem 0 0.6rem" }}>
        <h2><img src={glyphRepeat} width={17} height={17} alt="" /> What repeats</h2>
        <span className="muted row" style={{ fontSize: "var(--fs-xxs)", gap: "0.3rem" }}><span className="postmark" style={{ width: 14, height: 14 }} /> detected on device</span>
      </div>
      <div className="repeats-grid">
        {rows.map((r) => (
          <div key={r.merchant} className="repeats-card">
            <div className="repeats-headline" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {r.merchant}, {r.cadence === "irregular" ? "now and then" : r.cadence}, <span className="tnum">{fmtMajor(r.typical_amount, cur)}</span>{annual(r) > 0 ? <> — <span className="tnum">{fmtMajor(annual(r), cur)}</span> a year.</> : <> — {r.occurrences} times so far.</>}
            </div>
            <div className="repeats-cadence">{categoryLabel(r.category)} · seen {r.occurrences}× since {fmtDate(r.first_seen)}</div>
            {annual(r) > 0 && yearly > 0 && <div className="repeats-annual tnum">{Math.round((annual(r) / yearly) * 100)}% of the {fmtMajor(yearly, cur)} that repeats each year</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountsSection({ accounts, overview, onImport, onDelete }: { accounts: Account[]; overview: Overview; onImport: () => void; onDelete: (a: Account) => void }) {
  if (accounts.length === 0) return null;
  return (
    <>
      <div className="sec-head">
        <h2><Wallet size={17} style={{ color: "var(--accent)" }} /> Accounts</h2>
        <button className="btn btn-sm btn-pill" onClick={onImport}>Import to account</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
        {accounts.map((a) => (
          <div key={a.id} className="tile">
            <div className="tile-icon">{a.kind === "card" ? <Receipt size={18} /> : <Landmark size={18} />}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>
              <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{a.kind}</div>
            </div>
            <button className="tile-del" title="Delete account" aria-label="Delete account" onClick={() => onDelete(a)}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 8, display: "inline-flex", gap: "0.3rem", alignItems: "center" }}>
        <Lock size={10} /> {overview.currency} figures · stored on this device unless you back it up
      </div>
    </>
  );
}

function TxnList({ txns, accountName, onChanged }: { txns: Txn[]; accountName: (id: string) => string; onChanged: () => void }) {
  if (txns.length === 0) {
    return <div className="card" style={{ padding: "1.5rem", textAlign: "center" }}><span className="muted" style={{ fontSize: "var(--fs-sm)" }}>No transactions yet.</span></div>;
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
      {txns.map((t) => <TxnTile key={t.id} t={t} accountName={accountName} onChanged={onChanged} />)}
    </div>
  );
}

function TxnTile({ t, accountName, onChanged }: { t: Txn; accountName: (id: string) => string; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const out = t.amount < 0;
  return (
    <div className="tile">
      <div className="tile-icon" style={{ background: "var(--elev-2)", color: "var(--ink-3)" }}>{out ? <Receipt size={16} /> : <Coins size={16} />}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.merchant || t.raw_description}</div>
        <div className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>
          {fmtDate(t.posted_on)} · {accountName(t.account_id)}
        </div>
        {editing ? (
          <select
            value={t.category}
            onChange={async (e) => { await store.setCategory(t.id, e.target.value); setEditing(false); onChanged(); }}
            style={{ marginTop: 6, fontSize: "var(--fs-xs)", padding: "0.3rem 0.5rem", width: "auto", maxWidth: 220 }}
            autoFocus
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
          </select>
        ) : (
          <button className="chip" style={{ marginTop: 6, minHeight: 26, fontSize: 11, padding: "0.15rem 0.5rem" }} onClick={() => setEditing(true)} title="Change category">
            {categoryLabel(t.category)}
          </button>
        )}
      </div>
      <div className={`tnum ${out ? "amt-out" : "amt-in"}`} style={{ fontSize: "var(--fs-sm)", fontWeight: 600, flexShrink: 0 }}>
        {out ? "" : "+"}{fmtMinor(t.amount, t.currency)}
      </div>
    </div>
  );
}

// ── The ONE AI feature: ask a plain-English question about your spending ────────
type AskErr = { code: BrainErrorCode | "advice" | "empty"; message: string };

function AskCard({ offline }: { offline: boolean }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [usedSlice, setUsedSlice] = useState<QuerySlice | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"answer" | "recap">("answer");
  const [fresh, setFresh] = useState(false);
  const [err, setErr] = useState<AskErr | null>(null);

  async function run(q: string, asRecap: boolean) {
    const query = q.trim();
    if (!query) return;
    setBusy(true); setErr(null); setAnswer(null); setUsedSlice(null); setFresh(false); setMode(asRecap ? "recap" : "answer");
    try {
      // PRE-LLM advice fence — block advice-seeking questions BEFORE any billed call.
      const refusal = await store.adviceCheck(query);
      if (refusal) { setErr({ code: "advice", message: refusal }); setBusy(false); return; }

      // Compute the scoped slice on-device, then send ONLY that on the ONE billed call.
      const slice = await store.buildQuerySlice({ question: query, today: todayLocal() });
      if (slice.window_txn_count === 0) {
        setErr({ code: "empty", message: `No transactions in ${slice.window_label}. Import a statement covering that period, then ask again.` });
        setBusy(false); return;
      }
      const out = asRecap ? (await draftRecap(slice)).recap : (await answerQuestion(slice)).answer;
      if (!out) { setErr({ code: "empty", message: "No answer came back — try rephrasing your question." }); }
      else { setAnswer(out); setUsedSlice(slice); setFresh(true); setTimeout(() => setFresh(false), 700); }
    } catch (e) {
      if (e instanceof BrainError) {
        if (e.message === "advice_blocked") setErr({ code: "advice", message: "That asks for advice — Statements only describes your numbers." });
        else setErr({ code: e.code, message: e.message });
      } else if (e instanceof Error && e.message === "advice_blocked") {
        setErr({ code: "advice", message: "That asks for advice — Statements only describes your numbers." });
      } else {
        setErr({ code: "unavailable", message: "Couldn't get an answer — please try again." });
      }
    } finally { setBusy(false); }
  }

  return (
    <div className={`recap${fresh ? " fresh" : ""}`} style={{ padding: "1.1rem 1.25rem", marginBottom: "1.25rem" }}>
      <div className="eyebrow" style={{ flexWrap: "wrap" }}>
        <Sparkles size={14} style={{ flexShrink: 0 }} /> Ask your spending
      </div>

      <form className="row" style={{ gap: "0.5rem", marginTop: "0.7rem", flexWrap: "nowrap" }} onSubmit={(e) => { e.preventDefault(); void run(question, false); }}>
        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
          <Search size={15} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)", pointerEvents: "none" }} />
          <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="How much on groceries last month?"
            style={{ paddingLeft: 32 }} disabled={busy} />
        </div>
        <button type="submit" className="btn btn-primary btn-pill" style={{ flexShrink: 0, padding: "0 1.1rem" }} disabled={busy || offline || !question.trim()}>
          {busy && mode === "answer" ? "…" : <><Send size={14} /> Ask</>}
        </button>
      </form>

      {!answer && !busy && !err && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.7rem" }}>
          {EXAMPLE_QUESTIONS.slice(0, 4).map((ex) => (
            <button key={ex} className="chip chip-suggest" disabled={offline} onClick={() => { setQuestion(ex); void run(ex, false); }}>{ex}</button>
          ))}
        </div>
      )}

      <div className="row" style={{ marginTop: "0.6rem", gap: "0.5rem" }}>
        <button className="btn btn-sm btn-pill" disabled={busy || offline}
          onClick={() => void run(question.trim() || "Summarise my spending for last month.", true)}>
          {busy && mode === "recap" ? "Drafting…" : "Draft a recap"}
        </button>
        <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>Only a small spending summary is sent — never your statements.</span>
      </div>

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.9rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} /> Reading your pre-computed totals…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "68%" }} />
        </div>
      )}

      {err && !busy && <AskError err={err} onRetry={() => void run(question, mode === "recap")} />}

      {answer && !busy && (
        <div className="fade-up" style={{ marginTop: "0.85rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{answer}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {usedSlice && <FactList facts={usedSlice.headline_facts} />}
          <div className="row" style={{ marginTop: 12 }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>Describes your imported transactions — not financial, budgeting, or tax advice.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function AskError({ err, onRetry }: { err: AskErr; onRetry: () => void }) {
  // Insufficient funds (402) — point to the Oriora account, NO amount shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: 12 }}>
        <Coins size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Add funds to keep asking</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then ask again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "advice") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={15} style={{ color: "var(--ink-3)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  // unavailable → retryable
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
        <RefreshCw size={12} /> Retry
      </button>
    </div>
  );
}

// The receipts: the deterministic, pre-ranked facts the ONE answer drew from. Purely
// observational (totals/deltas/merchants) — the same headline_facts the brain narrated.
function FactList({ facts }: { facts: HeadlineFact[] }) {
  const rows = facts.slice(0, 5);
  if (rows.length === 0) return null;
  const dotClass = (t: FactTone) => (t === "up" ? "up" : t === "down" ? "down" : "");
  return (
    <div style={{ marginTop: "0.85rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border-subtle)" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>The numbers behind this</div>
      {rows.map((f, i) => (
        <div key={i} className="fact">
          <span className={`fact-dot ${dotClass(f.tone)}`} />
          <span className="fact-text">{f.text}</span>
        </div>
      ))}
    </div>
  );
}


// ── Import sheet: pick a file, preview the parse, name an account, commit ────────
function ImportSheet({ onClose, onImported, accounts }: { onClose: () => void; onImported: () => void; accounts: Account[] }) {
  const isWeb = platform() === "web"; // web has no filesystem path and no Rust PDF parser — CSV via file content
  const [path, setPath] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null); // web only: the picked file's text
  const [filename, setFilename] = useState<string | null>(null);
  const [preview, setPreview] = useState<ParsePreview | null>(null);
  const [accountName, setAccountName] = useState("");
  const [accountKind, setAccountKind] = useState("bank");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function autoName(name: string) {
    if (!accountName) {
      const base = name.replace(/\.(csv|pdf|txt)$/i, "").replace(/[_-]+/g, " ").trim();
      setAccountName(base.length > 2 ? base.slice(0, 32) : "My account");
    }
  }

  // Web: a plain <input type=file> (openFileDialog is the Tauri plugin — it does not exist in a
  // browser; measured live 2026-08-19: the button did nothing and the whole import was dead on web).
  function pickFileWeb() {
    setErr(null);
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.txt,text/csv,text/plain";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const fr = new FileReader();
      fr.onerror = () => setErr("Couldn't read that file — try again.");
      fr.onload = () => {
        void (async () => {
          setFilename(file.name);
          setContent(String(fr.result));
          setPreview(null);
          setBusy(true);
          try {
            const pv = await store.parseStatement({ content: String(fr.result) });
            setPreview(pv);
            autoName(file.name);
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          } finally {
            setBusy(false);
          }
        })();
      };
      fr.readAsText(file);
    };
    input.click();
  }

  async function pickFile() {
    if (isWeb) { pickFileWeb(); return; }
    setErr(null);
    const selected = await openFileDialog({
      multiple: false,
      filters: [{ name: "Statements", extensions: ["csv", "pdf", "txt"] }],
    });
    const chosen = Array.isArray(selected) ? selected[0] : selected;
    if (!chosen) return;
    const name = chosen.split("/").pop() ?? chosen;
    setPath(chosen);
    setFilename(name);
    setPreview(null);
    setBusy(true);
    try {
      // Rust reads + parses the file at `chosen` directly (no WebView fs scope needed).
      const pv = await store.parseStatement({ path: chosen });
      setPreview(pv);
      autoName(name);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if ((!path && !content) || !preview) return;
    setBusy(true); setErr(null);
    try {
      await store.importStatement({
        path: path ?? undefined, content: content ?? undefined,
        accountName: accountName.trim() || "My account", accountKind,
      });
      onImported();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <BottomSheet title="Import a statement" onClose={onClose}>
      <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, margin: "0 0 1rem" }}>
        {isWeb ? (
          <>Choose a bank/card export. <strong>CSV works for most banks.</strong> PDF import needs the
          iPhone app — in the browser, use your bank's CSV export. Everything is parsed right here in
          your browser.</>
        ) : (
          <>Choose a bank/card export. <strong>CSV works for most banks.</strong> PDF works for common single-line
          layouts; scanned-image PDFs aren’t supported — use the CSV export instead. Everything is parsed on this device.</>
        )}
      </p>

      {!preview && (
        <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 48 }} onClick={() => void pickFile()} disabled={busy}>
          <FileText size={16} /> {busy ? "Reading…" : filename ? "Choose a different file" : isWeb ? "Choose file (CSV)" : "Choose file (CSV or PDF)"}
        </button>
      )}

      {filename && !preview && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.6rem", textAlign: "center" }}>{filename}</div>
      )}

      {err && <div className="notice notice-err" style={{ marginTop: "0.85rem" }}><span style={{ fontSize: "var(--fs-xs)" }}>{err}</span></div>}

      {preview && (
        <div className="fade-up" style={{ marginTop: "0.25rem" }}>
          <div className="card" style={{ padding: "0.85rem 1rem", background: "var(--accent-soft)", border: "1px solid var(--accent-line)" }}>
            <div style={{ fontWeight: 600, fontSize: "var(--fs-md)", color: "var(--accent-soft-ink)" }}>{preview.parsed.toLocaleString()} transactions found</div>
            <div style={{ fontSize: "var(--fs-xs)", marginTop: 2, color: "var(--accent-soft-ink)", opacity: 0.85 }}>{preview.format.toUpperCase()} · {preview.currency} · {filename}</div>
            {preview.sample.length > 0 && (
              <div style={{ marginTop: "0.7rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.35rem" }}>
                {preview.sample.slice(0, 4).map((s, i) => (
                  <div key={i} className="row tnum" style={{ justifyContent: "space-between", fontSize: 12, color: "var(--accent-soft-ink)" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "65%" }}>{fmtDate(s.posted_on)} · {s.merchant || s.raw_description}</span>
                    <span className={s.amount < 0 ? "amt-out" : "amt-in"}>{fmtMajor(s.amount / 100, s.currency)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ marginTop: "1rem" }}>
            <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--ink-2)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.05em" }}>Account name</div>
            <input list="account-names" value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="e.g. Monzo current" />
            <datalist id="account-names">
              {accounts.map((a) => <option key={a.id} value={a.name} />)}
            </datalist>
            <div className="row" style={{ gap: "0.4rem", marginTop: "0.6rem" }}>
              {(["bank", "card", "other"] as const).map((k) => (
                <button key={k} type="button" className={`chip${accountKind === k ? " selected" : ""}`} onClick={() => setAccountKind(k)}>{k}</button>
              ))}
            </div>
            <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 8 }}>Re-importing into an existing name merges in — duplicate rows are skipped automatically.</div>
          </div>

          <div className="row" style={{ gap: "0.6rem", marginTop: "1.2rem", flexWrap: "nowrap" }}>
            <button className="btn btn-pill" style={{ flex: 1 }} onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn btn-primary btn-pill" style={{ flex: 2 }} onClick={() => void commit()} disabled={busy}>{busy ? "Importing…" : `Import ${preview.parsed.toLocaleString()}`}</button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

// Bottom sheet — iOS-native feel; slides up from the bottom edge.
function BottomSheet({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-bottom" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-grab" />
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem", letterSpacing: "-0.01em" }}>{title}</div>
          <button className="tile-del" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </>
  );
}

// Centered modal — for the small destructive confirm.
function CenterModal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-center" role="dialog" aria-modal="true" aria-label={title}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.9rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.15rem" }}>{title}</div>
          <button className="tile-del" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
        {children}
      </div>
    </>
  );
}
